import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { appApi } from "@/lib/appApi";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

const TOKEN_KEY = "wv_token";

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, mode: "login", after: null, step: "form", challenge: "email", preToken: "", email: "", notice: "" });
  const bootDone = useRef(false);

  const setAuth = useCallback((tok, usr) => {
    if (!tok) return;
    localStorage.setItem(TOKEN_KEY, tok);
    setToken(tok);
    if (usr) setUser(usr);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const refreshMe = useCallback(
    async (tok) => {
      const t = tok || localStorage.getItem(TOKEN_KEY);
      if (!t) return;
      try {
        const d = await appApi("/api/auth/me", { token: t });
        setUser(d.user || d);
      } catch {
        logout();
      }
    },
    [logout]
  );

  // Boot: handle Google OAuth return (#session_id=...) first, else load /me.
  useEffect(() => {
    if (bootDone.current) return;
    bootDone.current = true;
    (async () => {
      const hash = window.location.hash || "";
      const hashParams = new URLSearchParams(hash.replace(/^#/, ""));
      const queryParams = new URLSearchParams(window.location.search || "");
      const sid = hashParams.get("session_id") || queryParams.get("session_id");
      const googleError = queryParams.get("google_error");
      if (sid || googleError) {
        window.history.replaceState(null, "", window.location.pathname);
        if (googleError) {
          toast.error("Google sign-in was cancelled or could not be completed. Please try again.");
          setLoading(false);
          return;
        }
        if (sid) {
          try {
            const d = await appApi("/api/auth/google/session", { method: "POST", body: { session_id: sid } });
            const intent = (() => { try { return localStorage.getItem("wv_oauth_intent"); } catch { return null; } })();
            try { localStorage.removeItem("wv_oauth_intent"); } catch { /* ignore */ }
            const blockSignup = (email) => setDialog({ open: true, mode: "login", after: null, step: "form", challenge: "email", preToken: "", email: email || "", notice: "An account with this email already exists. Please log in instead." });
            if (d.token) {
              const u = d.user || {};
              const createdMs = u.created_at ? Date.parse(u.created_at) : 0;
              const isNew = createdMs ? Date.now() - createdMs < 120000 : false;
              if (intent === "signup" && !isNew) {
                blockSignup(u.email);
              } else {
                setAuth(d.token, d.user);
              }
            } else if (d.needs_2fa) {
              // A 2FA challenge always means the account already exists.
              if (intent === "signup") {
                blockSignup(d.email);
              } else {
                setDialog({ open: true, mode: "login", after: null, step: "verify", challenge: "2fa", preToken: d.pre_token || "", email: d.email || "" });
              }
            } else if (d.needs_verification) {
              setDialog({ open: true, mode: "login", after: null, step: "verify", challenge: "email", preToken: d.pre_token || "", email: d.email || "" });
            } else {
              toast.error("Google sign-in could not be completed. Please try again.");
            }
          } catch (e) {
            toast.error(e.message || "Google sign-in failed");
          }
        }
        setLoading(false);
        return;
      }
      if (localStorage.getItem(TOKEN_KEY)) await refreshMe();
      setLoading(false);
    })();
  }, [refreshMe, setAuth]);

  const register = useCallback((payload) => appApi("/api/auth/register", { method: "POST", body: payload }), []);
  const resendOtp = useCallback((pre_token) => appApi("/api/auth/resend-otp", { method: "POST", body: { pre_token } }), []);
  const send2faRecovery = useCallback((pre_token) => appApi("/api/auth/2fa/send-recovery", { method: "POST", body: { pre_token } }), []);
  const verifyEmail = useCallback(
    async (payload) => {
      const d = await appApi("/api/auth/verify-email", { method: "POST", body: payload });
      setAuth(d.token, d.user);
      return d;
    },
    [setAuth]
  );
  const login = useCallback(
    async (payload) => {
      const d = await appApi("/api/auth/login", { method: "POST", body: payload });
      if (d.token) setAuth(d.token, d.user);
      return d;
    },
    [setAuth]
  );

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginGoogle = useCallback((intent = "login") => {
    try { localStorage.setItem("wv_oauth_intent", typeof intent === "string" ? intent : "login"); } catch { /* ignore */ }
    const redirectUrl = window.location.origin + "/";
    // Use the app's OWN Google OAuth (branded "Warranty_Vault pro") instead of the shared Emergent login.
    window.location.href = `${process.env.REACT_APP_APP_API}/api/auth/google/start?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const loginApple = useCallback(async (payload) => {
    const d = await appApi("/api/auth/apple", { method: "POST", body: payload });
    setAuth(d.token, d.user);
    return d;
  }, [setAuth]);

  const verify2fa = useCallback(async (payload) => {
    const d = await appApi("/api/auth/verify-2fa", { method: "POST", body: payload });
    setAuth(d.token, d.user);
    return d;
  }, [setAuth]);

  const startCheckout = useCallback(
    async (planKey) => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (!t) throw new Error("Please sign in first");
      const origin = window.location.origin;
      const d = await appApi("/api/payments/checkout", {
        method: "POST",
        token: t,
        body: { plan_key: planKey, origin, success_url: `${origin}/thank-you`, cancel_url: `${origin}/#pricing` },
      });
      if (d.url) window.location.href = d.url;
      return d;
    },
    []
  );

  const completeAge = useCallback(
    async (payload) => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (!t) throw new Error("Please sign in first");
      const d = await appApi("/api/account/complete-age", { method: "POST", token: t, body: payload });
      await refreshMe(t);
      return d;
    },
    [refreshMe]
  );

  const openAuth = useCallback((mode = "login", after = null) => setDialog({ open: true, mode, after, step: "form", challenge: "email", preToken: "", email: "", notice: "" }), []);
  const openChallenge = useCallback(({ challenge, pre_token, email, after = null }) => setDialog({ open: true, mode: "login", after, step: "verify", challenge, preToken: pre_token || "", email: email || "", notice: "" }), []);
  const closeAuth = useCallback(() => setDialog((d) => ({ ...d, open: false })), []);

  const value = {
    token, user, loading, dialog,
    register, resendOtp, send2faRecovery, verifyEmail, verify2fa, login, loginGoogle, loginApple,
    logout, refreshMe, startCheckout, completeAge, openAuth, openChallenge, closeAuth,
  };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
